import { forwardRef, useCallback, useState } from 'react'

import { isEmpty } from 'ramda'

import { WarningAmberRounded, WarningRounded } from '@mui/icons-material'
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { tableCellClasses } from '@mui/material/TableCell'

import { TableVirtuoso } from 'react-virtuoso'

import { FadeInOnMount } from '@common/components'
import { generageVisibleTextColor } from '@common/utils/colorsGenerator'
import { formatDayWithOrdinal } from '@common/utils/formatters'

import { getLayerColor, getLayerFormattedValue } from '../utils'
import ConnectivityPDF from './ConnectivityPDF'
import DetailPopover from './DetailPopover'
import {
  LAYER_AP,
  LAYER_AWS_STATUS,
  LAYER_CONN_GUIDANCE_MODE,
  LAYER_ERROR,
  LAYER_FW,
  LAYER_MIC,
  LAYER_NOISE,
  LAYER_OPERATIONAL_STATE,
  LAYER_PROVISION,
  LAYER_RESET_REASON,
  LAYER_RSSI,
  LAYER_SNOOZE,
  LAYER_SSID,
  LAYER_SSID_MIGRATION,
  LAYER_STATUS,
} from './LayerSelector'

const VirtuosoTableComponents = {
  Scroller: forwardRef((props, ref) => (
    <TableContainer component={Box} {...props} ref={ref} />
  )),
  Table: (props) => (
    <Table
      {...props}
      padding="none"
      sx={{
        borderCollapse: 'collapse',
        tableLayout: 'fixed',
        [`& .${tableCellClasses.root}`]: {
          borderBottom: 'none',
        },
      }}
    />
  ),
  TableHead: forwardRef((props, ref) => <TableHead {...props} ref={ref} />),
  TableRow,
  TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
}

export const UNIT_CELL_WIDTH = 120

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {Object} props.property
 * @param {Object} props.data
 * @param {Object[]} props.columns
 * @param {Object[]} props.rows
 * @param {string} props.layer
 * @param {boolean} props.pdfExporting
 * @param {Function} props.onProvisionerModeEnabled
 */
export default function UnitsMap({
  property,
  data,
  layer,
  columns,
  rows,
  pdfExporting,
  onProvisionerModeEnabled,
}) {
  const [popoverData, setPopoverData] = useState(null)

  const getUnitBackground = useCallback(
    (unit) => {
      if (!unit || !unit.devices || !data) return null
      return getLayerColor({ unit, layer, legend: data.table.legend })
    },
    [layer, data],
  )

  const tooltipProps = {
    followCursor: true,
    slotProps: {
      popper: {
        modifiers: [{ name: 'offset', options: { offset: [0, 6] } }],
      },
    },
  }

  const getUnitTooltip = useCallback(
    ({ unit, column }) => {
      if (!unit || isEmpty(unit)) return null
      const messagePrefix = {
        [LAYER_STATUS]: 'Status',
        [LAYER_SNOOZE]: 'Snooze',
        [LAYER_AWS_STATUS]: 'AWS Status',
        [LAYER_PROVISION]: 'Provision State',
        [LAYER_OPERATIONAL_STATE]: 'Operational State',
        [LAYER_CONN_GUIDANCE_MODE]: 'Connectivity Guidance Mode',
        [LAYER_RSSI]: 'RSSI',
        [LAYER_AP]: 'AP MAC',
        [LAYER_SSID]: 'SSID',
        [LAYER_SSID_MIGRATION]: 'SSID Migration Status',
        [LAYER_RESET_REASON]: 'Reset Reason',
        [LAYER_ERROR]: 'Error Conditions',
        [LAYER_FW]: 'Firmware',
        [LAYER_NOISE]: 'Noise Enabled',
        [LAYER_MIC]: 'Has Microphone',
      }

      const warningNoFloor = !unit.floor
        ? 'Warning: Floor not explicitly specified'
        : null
      const warningInvalidFloor =
        column === null && unit.floor
          ? `Warning: Floor is invalid! (${unit.floor})`
          : null
      if (unit.devices.length > 1) {
        return [warningNoFloor, warningInvalidFloor, 'More than 1 device detected!']
          .filter((msg) => msg)
          .join('\n')
      }

      const device = unit.devices?.[0]
      const formattedValue = getLayerFormattedValue({ unit, device, layer })
      const message = `${messagePrefix[layer]}: ${formattedValue}`

      return [warningNoFloor, warningInvalidFloor, message]
        .filter((msg) => msg)
        .join('\n')
    },
    [layer],
  )

  const headerContent = useCallback(
    () => (
      <TableRow>
        {columns.map((floor) => (
          <TableCell
            key={floor}
            variant="head"
            style={{ width: UNIT_CELL_WIDTH }}
            sx={{ backgroundColor: 'background.paper', p: 1 }}
          >
            <Tooltip
              {...tooltipProps}
              title={!floor ? 'The floor is set, but most likely incorrect!' : null}
            >
              <Card
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fafafa',
                  userSelect: 'none',
                  gap: 0.5,
                }}
              >
                {!floor && (
                  <WarningRounded sx={{ fontSize: 16, color: 'error.main' }} />
                )}
                <Typography
                  variant="h6"
                  fontSize={14}
                  fontWeight={600}
                  textAlign="center"
                >{`${floor ? formatDayWithOrdinal(floor) : ''} Floor`}</Typography>
              </Card>
            </Tooltip>
          </TableCell>
        ))}
      </TableRow>
    ),
    [columns],
  )

  const rowContent = useCallback(
    (index, row) => (
      <>
        {columns.map((column) => {
          const unit = row[column]

          const bgColor = getUnitBackground(unit)
          const textColor = generageVisibleTextColor({
            hexBackground:
              bgColor && !bgColor.startsWith('linear-gradient')
                ? bgColor
                : bgColor?.match(/#[0-9a-fA-F]{6}/)[0] ?? '#FFFFFF',
          })
          const tooltip = getUnitTooltip({ unit, column })
          const hoverColor =
            bgColor && !bgColor.startsWith('linear-gradient')
              ? alpha(bgColor, 0.7)
              : '#fafafa'

          const showErrIcon =
            unit && (!unit.floor || column === null || unit.devices.length > 1)

          const onUnitClick = (event) => {
            setPopoverData({ anchor: event.currentTarget, unit })
          }

          return (
            <TableCell
              key={`${column}:${unit?.name ?? index}`}
              style={{ width: UNIT_CELL_WIDTH }}
              sx={{ p: 1 }}
            >
              <FadeInOnMount>
                <Tooltip
                  {...tooltipProps}
                  title={
                    !!tooltip && <div style={{ whiteSpace: 'pre-line' }}>{tooltip}</div>
                  }
                >
                  <Card
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      userSelect: 'none',
                      background: bgColor,
                      '&:hover': {
                        opacity: unit && unit.devices.length <= 1 ? 1 : 0.75,
                        backgroundColor:
                          unit && unit.devices.length <= 1 ? hoverColor : null,
                        cursor: unit ? 'pointer' : 'default',
                      },
                      p: 0.5,
                    }}
                    onClick={unit ? onUnitClick : null}
                  >
                    {showErrIcon && (
                      <WarningAmberRounded sx={{ fontSize: 16, color: textColor }} />
                    )}
                    <Typography
                      fontSize={14}
                      color={textColor}
                      sx={{ userSelect: 'none' }}
                    >
                      {row[column]?.name ?? '--'}
                    </Typography>
                  </Card>
                </Tooltip>
              </FadeInOnMount>
            </TableCell>
          )
        })}
      </>
    ),
    [columns, layer],
  )

  return (
    <>
      {popoverData && (
        <DetailPopover
          data={popoverData}
          layer={layer}
          legend={data?.table?.legend}
          onProvisionerModeEnabled={async () =>
            onProvisionerModeEnabled(setPopoverData)
          }
          onClose={() => setPopoverData(null)}
        />
      )}
      <Box position="relative">
        {pdfExporting && (
          <ConnectivityPDF
            property={property}
            layer={layer}
            rows={rows}
            legend={data?.table?.legend ?? {}}
            components={VirtuosoTableComponents}
            fixedHeaderContent={headerContent}
            itemContent={rowContent}
          />
        )}
        <Box
          style={{
            height: 'calc(100vh - 582px)',
            minHeight: 650,
            width: '100%',
            marginTop: '24px',
          }}
        >
          {data?.table && (
            <TableVirtuoso
              id="connectivity_map_table"
              data={rows ?? []}
              components={VirtuosoTableComponents}
              fixedHeaderContent={headerContent}
              itemContent={rowContent}
            />
          )}
        </Box>
      </Box>
    </>
  )
}
